<script>
import {
  mapState, mapMutations, mapGetters, mapActions,
} from 'vuex';

import Feature from '../components/WhatIsMyServers/Feature.vue';

import i18n from '../i18n';
import store from '../store';

export default {
  i18n,
  store,
  name: 'Promo',
  components: {
    Feature,
  },
  computed: {
    ...mapState(['isPromoOpen', 'devEnv']),
    ...mapGetters(['features']),
    heading() {
      return 'Enhance your Unraid experience with these<br class="hidden sm:block"/> Connect <span class="title-beta text-grey-mid border-grey-mid text-10px uppercase relative py-2px px-4px border-2 rounded-full">BETA</span> features';
      // return this.$t('promoHeading').replace('(BETA)', `<span class="title-beta text-grey-mid border-grey-mid text-10px uppercase relative py-2px px-4px border-2 rounded-full">${i18n.tc('upc.myServers.beta')}</span>`);
    },
  },
  methods: {
    ...mapActions(['installPlugin']),
    ...mapMutations(['SET_IS_PROMO_OPEN']),
  },
  watch: {
    // no longer auto show promo once it's been closed
    isPromoOpen(newVal, oldVal) {
      if (oldVal && !newVal) {
        sessionStorage.removeItem('showPromoPostTrialInstall');
      }
    },
    // if the launchpad closes and we have the sessionStorage item show the promo
    isLaunchpadOpen(newVal, oldVal) {
      if (oldVal && !newVal && sessionStorage.getItem('showPromoPostTrialInstall')) {
        this.SET_IS_PROMO_OPEN(false);
      }
    },
  },
};
</script>

<template>
  <transition appear name="fade" mode="in-out">
    <div v-show="isPromoOpen" class="fixed inset-0 z-10 flex flex-col justify-start items-end mt-90px">
      <transition appear name="fade" mode="in-out">
        <div
          v-show="isPromoOpen"
          @click="SET_IS_PROMO_OPEN(false)"
          class="fixed inset-0 z-0 bg-beta opacity-70 cursor-pointer"
          :title="$t('closePromo')"></div>
      </transition>
      <transition appear name="slideVerticalFade" mode="in-out">
        <div v-show="isPromoOpen" class="Promo TopBlip text-beta bg-alpha border-grey-darkest text-center relative z-10 w-full max-w-800px mr-8px p-24px sm:p-32px lg:px-40px shadow-md rounded-lg">
          <h2 class="text-16px font-semibold my-0" v-html="heading"></h2>

          <div class="text-12px flex flex-wrap justify-center my-16px">
            <feature
              v-for="(feature, index) in features"
              :key="index"
              :title="feature.title"
              :copy="feature.copy"
              :center="feature.center || false"
            />
          </div>

          <div class="w-full max-w-xs flex flex-col mx-auto">
            <button v-if="devEnv" @click="installPlugin('dynamix.unraid.net.staging.plg')" class="button mb-8px">Install Staging</button>
            <button @click="installPlugin()" class="button">{{ $t('installPlugin') }}</button>
            <div>
              <a
                href="https://docs.unraid.net/category/unraid-connect"
                class="text-12px tracking-wide inline-block mx-8px opacity-60 hover:opacity-100 focus:opacity-100 underline mt-16px transition"
                target="_blank"
                rel="noopener noreferrer"
                :title="$t('checkoutTheMyServersDocs')"
              >{{ $t('learnMore') }}</a>
              <button
                @click="SET_IS_PROMO_OPEN(false)"
                class="text-12px tracking-wide inline-block mx-8px opacity-60 hover:opacity-100 focus:opacity-100 underline mt-16px transition"
                :title="$t('closePromo')"
              >
                {{ $t('noThanks') }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<style lang="postcss">
@tailwind base;
@tailwind components;
@tailwind utilities;
/*! purgecss start ignore */
.TopBlip {
  margin-top: -3px;

  &::before {
    @apply absolute z-20 block;

    content: '';
    width: 0;
    height: 0;
    top: -10px;
    right: 32px;
    border-right: 11px solid transparent;
    border-bottom: 11px solid var(--color-alpha);
    border-left: 11px solid transparent;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .15s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slideVerticalFade-enter-active, .slideVerticalFade-leave-active {
  transition: opacity .15s ease-out, transform .15s ease-out;
}

.slideVerticalFade-enter, .slideVerticalFade-leave-to {
  opacity: 0;
  transform: translateY(36px);
}

.slideHorizontalFade-enter-active, .slideHorizontalFade-leave-active {
  transition: opacity 1s ease-out, transform 1s ease-out;
}

.slideHorizontalFade-enter, .slideHorizontalFade-leave-to {
  opacity: 0;
  transform: translateX(36px);
}

.button {
  @apply text-orange;
  @apply font-semibold;
  @apply leading-none;
  @apply uppercase;
  @apply tracking-wide;
  @apply text-center;
  @apply no-underline;
  @apply bg-transparent;

  background:
    /* top */
    linear-gradient(
      to right,
      #e03237 0%,
      #fd8c3c 100%
    )
    left
    top
    no-repeat,
    /* bottom */
    linear-gradient(
      to right,
      #e03237 0%,
      #fd8c3c 100%
    )
    left
    bottom
    no-repeat,
    /* right */
    linear-gradient(
      to top,
      #e03237 0%,
      #e03237 100%
    )
    left
    bottom
    no-repeat,
    /* right */
    linear-gradient(
      to top,
      #fd8c3c 0%,
      #fd8c3c 100%
    )
    right
    bottom
    no-repeat;
  background-size: 100% 2px, 100% 2px, 2px 100%, 2px 100%;
  border: 0;

  &:active {
    transform: scale(.975);
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }

  &:hover,
  &:focus {
    @apply text-white;
    background: linear-gradient(to right, theme('colors.red') 0%, theme('colors.orange') 100%);
    box-shadow: 0;
  }
}

.Promo .button {
  @apply text-14px px-24px py-16px tracking-wide;
}

.title-beta { top: -4px; }
/*! purgecss end ignore */
</style>
